import { Grn } from '../../types/grn';
import { X, Calendar, Clock, User, FileText, Package } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyUtils';

interface GrnViewModalProps {
  grn: Grn;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal component for viewing finalized GRN details
 * Shows all GRN information in a read-only format
 */
export default function GrnViewModal({ grn, isOpen, onClose }: GrnViewModalProps) {
  if (!isOpen) return null;

  // Using the utility function from currencyUtils

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <Package className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              GRN #{grn.grn_number || 'Draft'}
            </h2>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              grn.status === 'finalized' 
                ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' 
                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
            }`}>
              {grn.status}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* GRN Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Received Date:</span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {grn.received_date ? new Date(grn.received_date).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Received Time:</span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {grn.received_time || 'N/A'}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Received By:</span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {(grn as Grn & { received_by?: string })?.received_by || 'N/A'}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Notes:</span>
              </div>
              <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                {grn.notes || 'No notes provided'}
              </p>
            </div>
          </div>

          {/* Items Table */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Received Items</h3>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
              {/* Fixed Header */}
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  Received Items ({grn.items?.length || 0} items)
                </h4>
              </div>
              
              {/* Scrollable Table Body */}
              <div className="max-h-96 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Item Code
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Ordered Qty
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Received Qty
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Unit Price
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        VAT
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Line Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                    {grn.items?.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono">
                          {item.item_code || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-xs truncate">
                          {item.description || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-right">
                          {item.ordered_qty || 0}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right font-medium">
                          {item.received_qty || 0}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-right">
                          R {(Number(item.unit_price) || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            item.vatable 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' 
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {item.vatable ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right font-medium">
                          R {(Number(item.line_total) || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Fixed Footer with Stats */}
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-t border-gray-200 dark:border-gray-600">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Total Items:</span> {grn.items?.length || 0} | 
                    <span className="font-medium ml-2">Quantities Received:</span> {grn.items?.reduce((sum, item) => sum + (parseInt(String(item.received_qty)) || 0), 0) || 0} | 
                    <span className="font-medium ml-2">Quantities Ordered:</span> {grn.items?.reduce((sum, item) => sum + (parseInt(String(item.ordered_qty)) || 0), 0) || 0}
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    <span className="text-gray-600 dark:text-gray-400">Net Total:</span> {formatCurrency(grn.items?.reduce((sum, item) => sum + (Number(item.line_total) || 0), 0) || 0)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Totals Summary */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Summary</h4>
            <div className="grid grid-cols-3 gap-4 text-sm mb-4">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Net Total:</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">
                  {formatCurrency(grn.items?.reduce((sum, item) => sum + (Number(item.line_total) || 0), 0) || 0)}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">VAT (15%):</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">
                  {formatCurrency(grn.items?.reduce((sum, item) => {
                    const lineTotal = Number(item.line_total) || 0;
                    return sum + (item.vatable ? lineTotal * 0.15 : 0);
                  }, 0) || 0)}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Total:</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">
                  {formatCurrency(grn.items?.reduce((sum, item) => {
                    const lineTotal = Number(item.line_total) || 0;
                    const vatAmount = item.vatable ? lineTotal * 0.15 : 0;
                    return sum + lineTotal + vatAmount;
                  }, 0) || 0)}
                </span>
              </div>
            </div>
            
            {/* Balance Row */}
            <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
              <div className="text-sm">
                <span className="text-gray-600 dark:text-gray-400">Balance (Ordered - Received):</span>
                <span className={`ml-2 font-medium ${
                  (grn.items?.reduce((sum, item) => sum + (parseInt(String(item.ordered_qty)) || 0), 0) || 0) - 
                  (grn.items?.reduce((sum, item) => sum + (parseInt(String(item.received_qty)) || 0), 0) || 0) > 0
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-green-600 dark:text-green-400'
                }`}>
                  {(grn.items?.reduce((sum, item) => sum + (parseInt(String(item.ordered_qty)) || 0), 0) || 0) - 
                   (grn.items?.reduce((sum, item) => sum + (parseInt(String(item.received_qty)) || 0), 0) || 0)}
                </span>
                <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                  ({grn.items?.reduce((sum, item) => sum + (parseInt(String(item.ordered_qty)) || 0), 0) || 0} ordered - {grn.items?.reduce((sum, item) => sum + (parseInt(String(item.received_qty)) || 0), 0) || 0} received)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
